import React, { useEffect } from 'react'
import { useNavigate } from 'react-router'
import { observer } from 'mobx-react-lite'

import { userManager } from '@/Core/Managers'
import type { UIManager } from '@/Core/Managers/UI'

import { DefaultLayout } from './Components/DefaultLayout/DefaultLayout'
import { DefaultLoadTemplate } from './Components/DefaultLoadTemplate'
import { restClient } from '@/Core'

import { CentrifugoConnector } from './Components/CentrifugoConnector'
import { ServiceWorkerUpdateWatcher } from '@/Ship/Pwa/ServiceWorkerUpdateWatcher'
import { UpdatePrompt } from '@/Ship/Pwa/Components/UpdatePrompt'
import { AppConfig } from '@/Ship/Configs/AppConfig'
import { isAuthFailure } from '@/Ship/Helpers/isAuthFailure'
import { useRouteDocumentTitle } from '@/Ship/Hooks/useRouteDocumentTitle'
import { LoginForm } from '@/Containers/PublicSection/AuthContainer/Components/LoginForm'
// Приховано разом із загальною кнопкою чату:
// import ChatButton from '@/Containers/CommunicationSection/ChatContainer/Components/ChatButton'
import ChatDrawer from '@/Containers/CommunicationSection/ChatContainer/Components/ChatDrawer'

interface DefaultLayoutTemplateProps {
    layoutContext?: UIManager
    children: React.ReactNode
}

// Функція перевірки доступу
export const accessFn = () => userManager.isAuthorized

// Компонент для відображення помилки 403
export const templateError403 = () => <LoginForm />

// Асинхронна функція завантаження даних перед рендером layout
// Виконується один раз при монтуванні Layout
// Завантажує дані користувача включаючи permissions для перевірки доступів до ресурсів
export const loadData = async () => {
    const startTime = Date.now()
    const minDuration = 1000

    // Чекаємо гідратацію стану з localStorage перед перевіркою isAuthorized.
    // Без цього при холодному старті isAuthorized ще має дефолтне значення false.
    await userManager.waitForHydration()

    if (userManager.isAuthorized) {
        // При відсутності інтернету — пропускаємо запит /me,
        // довіряємо збереженому стану isAuthorized з localStorage.
        // Без цього offline-запит впав би з TypeError і викинув би
        // користувача (у т.ч. водія до редиректу на /driver) на форму входу.
        if (!navigator.onLine) {
            const elapsed = Date.now() - startTime
            const remaining = minDuration - elapsed
            if (remaining > 0) {
                await new Promise((resolve) => setTimeout(resolve, remaining))
            }
        } else {
            try {
                // Завантажуємо дані користувача з бекенду
                // Відповідь повинна містити поле permissions з правами доступу
                const user = await restClient.get(AppConfig.restUserMe)

                if (!(user as any)?.id) {
                    userManager.logout(true)
                }

                const elapsed = Date.now() - startTime
                const remaining = minDuration - elapsed

                if (remaining > 0) {
                    await new Promise((resolve) => setTimeout(resolve, remaining))
                }
            } catch (error: any) {
                console.error('[loadData] Error loading user data:', { error })
                // Розлогінюємо ЛИШЕ при явній відмові автентифікації (401/403).
                // Раніше будь-яка нетранзиентна помилка — зокрема 500/502/504 —
                // означала logout, і деплой бекенду викидав усіх користувачів.
                if (isAuthFailure(error)) {
                    userManager.logout(true)
                }
            }
        }
    }

    const elapsed = Date.now() - startTime
    const remaining = minDuration - elapsed

    if (remaining > 0) {
        await new Promise((resolve) => setTimeout(resolve, remaining))
    }

    return null
}

// Компонент, який відображається під час виконання loadData
export const loadTemplate = DefaultLoadTemplate

export const DefaultLayoutTemplate = observer(({ layoutContext, children }: DefaultLayoutTemplateProps) => {
    const navigate = useNavigate()
    // Назва вкладки = назва розділу (pageConfig.title поточної сторінки)
    useRouteDocumentTitle()
    const isDriver = userManager.isAuthorized && userManager.hasRole('ROLE_DRIVER')

    useEffect(() => {
        if (isDriver) {
            navigate('/driver', { replace: true })
        }
    }, [isDriver, navigate])

    // Не рендеримо дефолтний лейаут поки йде редирект
    if (isDriver) return null

    return (
        <>
            <CentrifugoConnector />
            {/* Оновлення PWA: без цього новий SW після деплою лишався "waiting",
                а диспетчери бачили старий бандл до закриття всіх вкладок */}
            <ServiceWorkerUpdateWatcher />
            <UpdatePrompt />
            <DefaultLayout layoutContext={layoutContext} children={children} />
            {/* Чат: плаваюча кнопка з лічильником непрочитаних + дровер */}
            {/* Загальну кнопку чату приховано на проді (закоментовано за вимогою) */}
            {/* <ChatButton /> */}
            <ChatDrawer />
        </>
    )
})
